import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Heart, MessageCircle, Share2, User, Building2, Clock, Send, X, Plus, Upload, Trash2, Filter } from 'lucide-react';
import './PostsListing.css';
import Dashboard from './Dashboard.js';

// Memoized components to prevent unnecessary re-renders
const PostCard = React.memo(({ post, imageUrls, onLikeToggle, onOpenComments, formatTimeAgo }) => {
  const handleLikeClick = useCallback(() => {
    onLikeToggle(post.post_id);
  }, [onLikeToggle, post.post_id]);

  const handleCommentsClick = useCallback(() => {
    onOpenComments(post);
  }, [onOpenComments, post]);

  return (
    <div className="post-card">
      {/* Post Header */}
      <div className="post-header">
        <div className="author-info">
          <div className="author-avatar">
            <User size={20} />
          </div>
          <div className="author-details">
            <div className="author-name">
              {post.first_name} {post.last_name}
            </div>
            {post.name && (
              <div className="company-info">
                <Building2 size={14} />
                <span>{post.name}</span>
              </div>
            )}
          </div>
        </div>
        <div className="post-time">
          <Clock size={14} />
          <span>{formatTimeAgo(post.created_at)}</span>
        </div>
      </div>

      {/* Post Content */}
      <div className="post-content">
        <h3 className="post-title">{post.title}</h3>
        <p className="post-text">{post.content}</p>
        
        {/* Post Images */}
        {post.image_path && post.image_path.length > 0 && (
          <PostImages 
            postId={post.post_id}
            imagePaths={post.image_path}
            imageUrls={imageUrls}
          />
        )}
      </div>

      {/* Post Actions */}
      <div className="post-actions">
        <button 
          className={`action-btn ${post.has_liked ? 'liked' : ''}`}
          onClick={handleLikeClick}
        >
          <Heart size={18} fill={post.has_liked ? "currentColor" : "none"} />
          <span>{post.num_of_likes}</span>
        </button>
        
        <button 
          className="action-btn"
          onClick={handleCommentsClick}
        >
          <MessageCircle size={18} />
          <span>{post.num_of_comments}</span>
        </button>
        
        <button className="action-btn">
          <Share2 size={18} />
          <span>Share</span>
        </button>
      </div>
    </div>
  );
});

// Memoized PostImages component
const PostImages = React.memo(({ postId, imagePaths, imageUrls }) => {
  return (
    <div className="post-images">
      {imagePaths.slice(0, 4).map((_, index) => {
        const imageKey = `${postId}-${index}`;
        const imageUrl = imageUrls[imageKey];
        
        return (
          <div key={index} className="post-image">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={`Post image ${index + 1}`}
                loading="lazy"
              />
            ) : (
              <div className="image-placeholder">
                <div className="loading-spinner"></div>
              </div>
            )}
            {index === 3 && imagePaths.length > 4 && (
              <div className="more-images-overlay">
                +{imagePaths.length - 4}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
});

// Memoized Comment component
const CommentItem = React.memo(({ comment, formatTimeAgo }) => {
  return (
    <div className="comment-item">
      <div className="comment-avatar">
        <User size={16} />
      </div>
      <div className="comment-content">
        <div className="comment-header">
          <span className="comment-author">{comment.names}</span>
          <span className="comment-time">{formatTimeAgo(comment.created_at)}</span>
        </div>
        <p className="comment-text">{comment.content}</p>
      </div>
    </div>
  );
});

const PostsListing = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [imageUrls, setImageUrls] = useState({});
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('all');
  const POSTS_PER_PAGE = 10;

  // Create Post Modal State
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [createPostData, setCreatePostData] = useState({
    title: '',
    content: '',
    files: []
  });
  const [creatingPost, setCreatingPost] = useState(false);
  const [createPostError, setCreatePostError] = useState('');

  // Memoized formatTimeAgo function
  const formatTimeAgo = useCallback((dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    return date.toLocaleDateString();
  }, []);

  // Optimized throttle function
  const throttle = useCallback((func, delay) => {
    let timeoutId;
    let lastExecTime = 0;
    return function (...args) {
      const currentTime = Date.now();
      
      if (currentTime - lastExecTime > delay) {
        func.apply(this, args);
        lastExecTime = currentTime;
      } else {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          func.apply(this, args);
          lastExecTime = Date.now();
        }, delay - (currentTime - lastExecTime));
      }
    };
  }, []);

  // Debounced image loading with batch processing
  const loadPostImages = useCallback(async (posts) => {
    if (!posts.length) return;

    // Process images in smaller batches to avoid overwhelming the browser
    const batchSize = 5;
    const batches = [];
    
    for (let i = 0; i < posts.length; i += batchSize) {
      batches.push(posts.slice(i, i + batchSize));
    }

    for (const batch of batches) {
      const imagePromises = [];
      const imageUrlsCache = {};

      batch.forEach(post => {
        if (post.image_path && post.image_path.length > 0) {
          // Only load first 2 images initially for performance
          post.image_path.slice(0, 2).forEach((_, index) => {
            const imageKey = `${post.post_id}-${index}`;
            imagePromises.push(
              getPostImageUrl(post.post_id, index).then(url => {
                if (url) {
                  imageUrlsCache[imageKey] = url;
                }
              }).catch(err => {
                console.warn(`Failed to load image ${imageKey}:`, err);
              })
            );
          });
        }
      });

      try {
        await Promise.all(imagePromises);
        setImageUrls(prev => ({ ...prev, ...imageUrlsCache }));
      } catch (error) {
        console.warn('Some images failed to load:', error);
      }

      // Small delay between batches to prevent UI blocking
      if (batches.indexOf(batch) < batches.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
  }, []);

  // Memoized API endpoint
  const getApiEndpoint = useCallback((pageNum = 1) => {
    const baseUrl = process.env.REACT_APP_API_URL;
    const params = `page=${pageNum}&limit=${POSTS_PER_PAGE}`;
    
    switch (filter) {
      case 'my-posts':
        return `${baseUrl}/posts/my-posts?${params}`;
      case 'liked':
        return `${baseUrl}/posts/liked-posts?${params}`;
      default:
        return `${baseUrl}/posts/list-posts?${params}`;
    }
  }, [filter, POSTS_PER_PAGE]);

  // Optimized scroll handler
  const handleScroll = useCallback(() => {
    if (loadingMore || !hasMore) return;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    if (scrollTop + windowHeight >= documentHeight - 200) {
      loadMorePosts();
    }
  }, [loadingMore, hasMore]);

  const throttledHandleScroll = useMemo(
    () => throttle(handleScroll, 300), // Increased throttle delay for better performance
    [throttle, handleScroll]
  );

  // Optimized loadMorePosts with abort controller
  const loadMorePosts = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    const abortController = new AbortController();

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setLoadingMore(false);
        return;
      }
      
      const nextPage = page + 1;
      const response = await fetch(getApiEndpoint(nextPage), {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal: abortController.signal
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.items.length === 0) {
          setHasMore(false);
        } else {
          setPosts(prevPosts => [...prevPosts, ...data.items]);
          setPage(nextPage);
          
          // Load images for new posts only
          loadPostImages(data.items);
          
          if (data.items.length < POSTS_PER_PAGE) {
            setHasMore(false);
          }
        }
      } else {
        console.error('Failed to load more posts');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading more posts:', error);
      }
    } finally {
      setLoadingMore(false);
    }

    return () => abortController.abort();
  }, [loadingMore, hasMore, page, getApiEndpoint, loadPostImages, POSTS_PER_PAGE]);

  // Optimized fetchPosts
  const fetchPosts = useCallback(async (pageNum = 1) => {
    const abortController = new AbortController();

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }
      
      const response = await fetch(getApiEndpoint(pageNum), {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal: abortController.signal
      });

      if (response.ok) {
        const data = await response.json();
        setPosts(data.items);
        setPage(pageNum);
        setHasMore(data.items.length === POSTS_PER_PAGE);
        
        // Load images asynchronously without blocking UI
        if (data.items.length > 0) {
          setTimeout(() => loadPostImages(data.items), 0);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch posts');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        setError('Failed to fetch posts');
        console.error('Error fetching posts:', error);
      }
    } finally {
      setLoading(false);
    }

    return () => abortController.abort();
  }, [getApiEndpoint, loadPostImages, POSTS_PER_PAGE]);

  // Optimized filter change handler
  const handleFilterChange = useCallback((newFilter) => {
    if (newFilter === filter) return; // Avoid unnecessary re-renders
    
    setFilter(newFilter);
    setLoading(true);
    setPosts([]);
    setPage(1);
    setHasMore(true);
    setImageUrls({}); // Clear image cache when changing filters
  }, [filter]);

  // Memoized event handlers
  const handleLikeToggle = useCallback(async (postId) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/posts/like/${postId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setPosts(prevPosts => 
          prevPosts.map(post => 
            post.post_id === postId 
              ? { 
                  ...post, 
                  has_liked: !post.has_liked,
                  num_of_likes: post.has_liked ? post.num_of_likes - 1 : post.num_of_likes + 1
                }
              : post
          )
        );
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  }, []);

  const handleOpenComments = useCallback((post) => {
    setSelectedPost(post);
    setShowComments(true);
    fetchComments(post.post_id);
  }, []);

  // Optimized image loading with caching
  const getPostImageUrl = useCallback(async (postId, index) => {
    const cacheKey = `${postId}-${index}`;
    
    // Check if already cached
    if (imageUrls[cacheKey]) {
      return imageUrls[cacheKey];
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/posts/get-post-image/${postId}/${index}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        
        // Cache the URL
        setImageUrls(prev => ({ ...prev, [cacheKey]: url }));
        
        return url;
      }
      return null;
    } catch (error) {
      console.error('Error fetching image:', error);
      return null;
    }
  }, [imageUrls]);

  // Other handlers remain the same but with useCallback...
  const fetchComments = useCallback(async (postId) => {
    setLoadingComments(true);
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/posts/comment/${postId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setComments(data.items);
      } else {
        console.error('Failed to fetch comments');
        setComments([]);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  }, []);

  const handleSubmitComment = useCallback(async () => {
    if (!newComment.trim() || !selectedPost) return;

    setSubmittingComment(true);
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/posts/comment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: newComment.trim(),
          post_id: selectedPost.post_id
        })
      });

      if (response.ok) {
        setNewComment('');
        await fetchComments(selectedPost.post_id);
        setPosts(prevPosts => 
          prevPosts.map(post => 
            post.post_id === selectedPost.post_id 
              ? { ...post, num_of_comments: post.num_of_comments + 1 }
              : post
          )
        );
      } else {
        console.error('Failed to submit comment');
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  }, [newComment, selectedPost, fetchComments]);

  const handleCloseComments = useCallback(() => {
    setShowComments(false);
    setSelectedPost(null);
    setComments([]);
    setNewComment('');
  }, []);

  const handleCreatePost = useCallback(async () => {
    if (!createPostData.title.trim() || !createPostData.content.trim()) {
      setCreatePostError('Title and description are required');
      return;
    }

    setCreatingPost(true);
    setCreatePostError('');

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      formData.append('title', createPostData.title);
      formData.append('description', createPostData.content);
      
      createPostData.files.forEach((file) => {
        formData.append('files', file);
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/posts/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        setCreatePostData({
          title: '',
          content: '',
          files: []
        });
        setShowCreatePost(false);
        
        if (filter === 'all' || filter === 'my-posts') {
          setLoading(true);
          setPosts([]);
          setPage(1);
          setHasMore(true);
          await fetchPosts(1);
        }
      } else {
        const errorData = await response.json();
        setCreatePostError(errorData.message || 'Failed to create post');
      }
    } catch (error) {
      setCreatePostError('Failed to create post');
      console.error('Error creating post:', error);
    } finally {
      setCreatingPost(false);
    }
  }, [createPostData, filter, fetchPosts]);

  const handleFileSelect = useCallback((e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = selectedFiles.filter(file => {
      const isValidType = file.type.startsWith('image/');
      const isValidSize = file.size <= 10 * 1024 * 1024;
      return isValidType && isValidSize;
    });

    if (validFiles.length !== selectedFiles.length) {
      setCreatePostError('Some files were rejected. Only images under 10MB are allowed.');
    } else {
      setCreatePostError('');
    }

    setCreatePostData(prev => ({
      ...prev,
      files: [...prev.files, ...validFiles].slice(0, 10)
    }));
  }, []);

  const removeFile = useCallback((index) => {
    setCreatePostData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  }, []);

  // Effects
  useEffect(() => {
    fetchPosts();
  }, [filter, fetchPosts]);

  useEffect(() => {
    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    return () => window.removeEventListener('scroll', throttledHandleScroll);
  }, [throttledHandleScroll]);

  // Cleanup image URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(imageUrls).forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  // Memoized rendered posts
  const renderedPosts = useMemo(() => {
    return posts.map((post) => (
      <PostCard
        key={post.post_id}
        post={post}
        imageUrls={imageUrls}
        onLikeToggle={handleLikeToggle}
        onOpenComments={handleOpenComments}
        formatTimeAgo={formatTimeAgo}
      />
    ));
  }, [posts, imageUrls, handleLikeToggle, handleOpenComments, formatTimeAgo]);

  // Memoized rendered comments
  const renderedComments = useMemo(() => {
    return comments.map((comment) => (
      <CommentItem
        key={comment.comment_id}
        comment={comment}
        formatTimeAgo={formatTimeAgo}
      />
    ));
  }, [comments, formatTimeAgo]);

  if (loading) {
    return (
      <Dashboard page={"posts"}>
        <div className="posts-container">
          <div className="loading-state">Loading posts...</div>
        </div>
      </Dashboard>
    );
  }

  if (error) {
    return (
      <Dashboard page={"posts"}>
        <div className="posts-container">
          <div className="error-state">Error: {error}</div>
        </div>
      </Dashboard>
    );
  }

  return (
    <Dashboard page={"posts"}>
      <div className="posts-container">
        <div className="posts-header">
          <div className="header-content">
            <h1>Community Posts</h1>
            <p>Discover what's happening in our community</p>
          </div>
          <button 
            className="create-post-btn"
            onClick={() => setShowCreatePost(true)}
          >
            <Plus size={20} />
            Create Post
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="filter-tabs">
          <button 
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => handleFilterChange('all')}
          >
            <Filter size={16} />
            All Posts
          </button>
          <button 
            className={`filter-tab ${filter === 'my-posts' ? 'active' : ''}`}
            onClick={() => handleFilterChange('my-posts')}
          >
            <User size={16} />
            My Posts
          </button>
          <button 
            className={`filter-tab ${filter === 'liked' ? 'active' : ''}`}
            onClick={() => handleFilterChange('liked')}
          >
            <Heart size={16} />
            Liked Posts
          </button>
        </div>

        <div className="posts-grid">
          {renderedPosts}
        </div>

        {/* Loading More Indicator */}
        {loadingMore && (
          <div className="loading-more">
            <div className="loading-spinner"></div>
            <span>Loading more posts...</span>
          </div>
        )}

        {/* End of Posts Indicator */}
        {!hasMore && posts.length > 0 && (
          <div className="end-of-posts">
            <span>You've reached the end of the posts</span>
          </div>
        )}

        {/* Empty State */}
        {posts.length === 0 && !loading && (
          <div className="empty-state">
            <div className="empty-icon">
              {filter === 'my-posts' ? <User size={48} /> : 
               filter === 'liked' ? <Heart size={48} /> : 
               <MessageCircle size={48} />}
            </div>
            <h3>
              {filter === 'my-posts' ? 'No posts yet' :
               filter === 'liked' ? 'No liked posts' :
               'No posts available'}
            </h3>
            <p>
              {filter === 'my-posts' ? 'Create your first post to get started!' :
               filter === 'liked' ? 'Start liking posts to see them here.' :
               'Be the first to share something with the community!'}
            </p>
            {filter !== 'liked' && (
              <button 
                className="create-post-btn"
                onClick={() => setShowCreatePost(true)}
              >
                <Plus size={20} />
                Create Post
              </button>
            )}
          </div>
        )}

        {/* Create Post Modal */}
        {showCreatePost && (
          <div className="modal-overlay" onClick={() => setShowCreatePost(false)}>
            <div className="create-post-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Create New Post</h3>
                <button className="close-btn" onClick={() => setShowCreatePost(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-content">
                {createPostError && (
                  <div className="error-message">{createPostError}</div>
                )}

                <div className="form-group">
                  <label htmlFor="post-title">Title *</label>
                  <input
                    id="post-title"
                    type="text"
                    value={createPostData.title}
                    onChange={(e) => setCreatePostData(prev => ({
                      ...prev,
                      title: e.target.value
                    }))}
                    placeholder="Enter post title..."
                    disabled={creatingPost}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="post-content">Description *</label>
                  <textarea
                    id="post-content"
                    value={createPostData.content}
                    onChange={(e) => setCreatePostData(prev => ({
                      ...prev,
                      content: e.target.value
                    }))}
                    placeholder="What's on your mind?"
                    rows={4}
                    disabled={creatingPost}
                  />
                </div>

                <div className="form-group">
                  <label>Images (Optional)</label>
                  <div className="file-upload-area">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileSelect}
                      disabled={creatingPost}
                      style={{ display: 'none' }}
                      id="file-input"
                    />
                    <label htmlFor="file-input" className="file-upload-btn">
                      <Upload size={20} />
                      Choose Images
                    </label>
                    <span className="file-upload-hint">
                      Max 10 images, 10MB each
                    </span>
                  </div>

                  {createPostData.files.length > 0 && (
                    <div className="selected-files">
                      {createPostData.files.map((file, index) => (
                        <div key={index} className="file-preview">
                          <img 
                            src={URL.createObjectURL(file)} 
                            alt={`Preview ${index + 1}`}
                            className="file-preview-image"
                          />
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="remove-file-btn"
                            disabled={creatingPost}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowCreatePost(false)}
                  disabled={creatingPost}
                  className="cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreatePost}
                  disabled={creatingPost || !createPostData.title.trim() || !createPostData.content.trim()}
                  className="submit-btn"
                >
                  {creatingPost ? (
                    <>
                      <div className="loading-spinner"></div>
                      Creating...
                    </>
                  ) : (
                    'Create Post'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Comments Modal */}
        {showComments && selectedPost && (
          <div className="comments-modal-overlay" onClick={handleCloseComments}>
            <div className="comments-modal" onClick={(e) => e.stopPropagation()}>
              <div className="comments-header">
                <h3>Comments</h3>
                <button className="close-btn" onClick={handleCloseComments}>
                  <X size={20} />
                </button>
              </div>

              <div className="original-post-summary">
                <div className="post-author">
                  <User size={16} />
                  <span>{selectedPost.first_name} {selectedPost.last_name}</span>
                </div>
                <h4>{selectedPost.title}</h4>
                <p>{selectedPost.content}</p>
              </div>

              <div className="comments-list">
                {loadingComments ? (
                  <div className="comments-loading">Loading comments...</div>
                ) : comments.length === 0 ? (
                  <div className="no-comments">No comments yet. Be the first to comment!</div>
                ) : (
                  renderedComments
                )}
              </div>

              <div className="add-comment-section">
                <div className="comment-input-container">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Write a comment..."
                    className="comment-input"
                    rows={3}
                    disabled={submittingComment}
                  />
                  <button
                    onClick={handleSubmitComment}
                    disabled={!newComment.trim() || submittingComment}
                    className="submit-comment-btn"
                  >
                    {submittingComment ? (
                      <div className="loading-spinner"></div>
                    ) : (
                      <Send size={16} />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Dashboard>
  );
};

export default PostsListing;